import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// ─── CACHE ────────────────────────────────────────────────────
const cache = new Map();
const CACHE_DURATION = 30 * 1000; // 30 seconds

const makeCacheKey = (url, params = {}) => {
  // Sab values ko string mein convert karo — number/string mismatch fix
  const normalized = Object.keys(params)
    .sort() // consistent order
    .reduce((acc, k) => {
      const v = params[k];
      // empty string, undefined, null — skip karo
      if (v === '' || v === undefined || v === null) return acc;
      acc[k] = String(v);
      return acc;
    }, {});
  return url + '::' + JSON.stringify(normalized);
};

const getCached = (key) => {
  const item = cache.get(key);
  if (!item) return null;
  if (Date.now() - item.timestamp > CACHE_DURATION) {
    cache.delete(key);
    return null;
  }
  return item.data;
};

const setCache = (key, data) => {
  cache.set(key, { data, timestamp: Date.now() });
};

// Decorator cache poori saaf karo — filter/search change pe
export const clearDecoratorCache = () => {
  for (const key of cache.keys()) {
    if (key.startsWith('/api/decorators')) cache.delete(key);
  }
};

// ─── INTERCEPTORS ─────────────────────────────────────────────
API.interceptors.request.use((config) => {
  const user = JSON.parse(localStorage.getItem('decorhub_user') || '{}');
  if (user.token) config.headers.Authorization = `Bearer ${user.token}`;
  return config;
}, (error) => Promise.reject(error));

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && error.response?.data?.code === 'TOKEN_EXPIRED' && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const user = JSON.parse(localStorage.getItem('decorhub_user') || '{}');
        if (user.refreshToken) {
          const { data } = await axios.post(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/auth/refresh-token`, {
            refreshToken: user.refreshToken
          });
          user.token = data.token;
          localStorage.setItem('decorhub_user', JSON.stringify(user));
          originalRequest.headers.Authorization = `Bearer ${data.token}`;
          return API(originalRequest);
        }
      } catch (_) {
        localStorage.removeItem('decorhub_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ─── CACHED GET ────────────────────────────────────────────────
const cachedGet = async (url, params = {}) => {
  const cacheKey = makeCacheKey(url, params);
  const cached = getCached(cacheKey);
  if (cached) return { data: cached };
  const response = await API.get(url, { params });
  setCache(cacheKey, response.data);
  return response;
};

// ─── APIS ──────────────────────────────────────────────────────
export const authAPI = {
  register:        (data)          => API.post('/api/auth/register', data),
  login:           (data)          => API.post('/api/auth/login', data),
  getMe:           ()              => API.get('/api/auth/me'),
  updateProfile:   (data)          => API.put('/api/auth/update-profile', data),
  changePassword:  (data)          => API.put('/api/auth/change-password', data),
  forgotPassword:  (email)         => API.post('/api/auth/forgot-password', { email }),
  resetPassword:   (token, password) => API.post(`/api/auth/reset-password/${token}`, { password }),
  verifyEmail:     (otp)           => API.post('/api/auth/verify-email', { otp }),
  resendOTP:       ()              => API.post('/api/auth/resend-otp'),
  refreshToken:    (refreshToken)  => API.post('/api/auth/refresh-token', { refreshToken }),
};

export const decoratorAPI = {
  getAll: (params) => {
    // Params clean karo — empty/null/undefined values backend tak mat bhejo
    const clean = Object.keys(params).reduce((acc, k) => {
      const v = params[k];
      if (v === '' || v === undefined || v === null || v === false) return acc;
      acc[k] = v;
      return acc;
    }, {});
    return API.get('/api/decorators', { params: clean });
  },
  getOne:          (id)   => cachedGet(`/api/decorators/${id}`),
  updateProfile:   (data) => API.put('/api/decorators/profile', data),
  uploadPortfolio: (data) => API.post('/api/decorators/portfolio', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deletePortfolio: (photoId) => API.delete(`/api/decorators/portfolio/${photoId}`),
  getDashboard:    ()     => API.get('/api/decorators/my/dashboard'),
  getEarningsChart: ()    => API.get('/api/decorators/my/earnings-chart'),
  blockDates:      (dates) => API.post('/api/decorators/availability/block', { dates }),
  unblockDate:     (date)  => API.delete('/api/decorators/availability/unblock', { data: { date } }),
};

export const bookingAPI = {
  create:           (data)                   => API.post('/api/bookings', data),
  getMyBookings:    (params)                 => API.get('/api/bookings/my', { params }),
  getOne:           (id)                     => API.get(`/api/bookings/${id}`),
  updateStatus:     (id, status, message)    => API.put(`/api/bookings/${id}/status`, { status, message }),
  cancel:           (id, reason)             => API.put(`/api/bookings/${id}/cancel`, { reason }),
  dispute:          (id, data)               => API.post(`/api/bookings/${id}/dispute`, data),
  checkAvailability:(decoratorId, month, year) => API.get(`/api/bookings/check-availability/${decoratorId}`, { params: { month, year } }),
  delete:           (id)                     => API.delete(`/api/bookings/${id}`),
};

export const reviewAPI = {
  create:               (data)         => API.post('/api/reviews', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getDecoratorReviews:  (id, params)   => cachedGet(`/api/reviews/decorator/${id}`, params),
  reply:                (id, reply)    => API.put(`/api/reviews/${id}/reply`, { reply }),
  markHelpful:          (id)           => API.post(`/api/reviews/${id}/helpful`),
};

export const paymentAPI = {
  createOrder: (bookingId, paymentType) => API.post('/api/payment/create-order', { bookingId, paymentType }),
  verify:      (data)                   => API.post('/api/payment/verify', data),
};

export const notificationAPI = {
  getAll:       (params) => API.get('/api/notifications', { params }),
  markRead:     (id)     => API.put(`/api/notifications/${id}/read`),
  markAllRead:  ()       => API.put('/api/notifications/mark-all-read'),
  delete:       (id)     => API.delete(`/api/notifications/${id}`),
};

export const messageAPI = {
  createConversation: (data)                    => API.post('/api/messages/conversation', data),
  getConversations:   ()                        => API.get('/api/messages/conversations'),
  getMessages:        (conversationId, params)  => API.get(`/api/messages/${conversationId}`, { params }),
  sendMessage:        (conversationId, data)    => API.post(`/api/messages/${conversationId}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
};

export const couponAPI = {
  validate: (code, totalAmount) => API.post('/api/coupons/validate', { code, totalAmount }),
};

export const adminAPI = {
  getStats:                ()              => API.get('/api/admin/stats'),
  getRevenueAnalytics:     (period)        => API.get('/api/admin/analytics/revenue', { params: { period } }),
  getEventAnalytics:       ()              => API.get('/api/admin/analytics/events'),
  getTopDecorators:        ()              => API.get('/api/admin/analytics/top-decorators'),
  getCommissionSummary:    ()              => API.get('/api/admin/analytics/commission-summary'),
  getPlatformInfo:         ()              => API.get('/api/admin/platform-info'),
  getDecorators:           (params)        => API.get('/api/admin/decorators', { params }),
  getDecoratorDetail:      (id)            => API.get(`/api/admin/decorators/${id}`),
  verifyDecorator:         (id, note)      => API.put(`/api/admin/decorators/${id}/verify`, { note }),
  unverifyDecorator:       (id, reason)    => API.put(`/api/admin/decorators/${id}/unverify`, { reason }),
  featureDecorator:        (id, months, featured) => API.put(`/api/admin/decorators/${id}/featured`, { months, featured }),
  collectCommission:       (decoratorId, amount, note, bookingId) => API.put(`/api/admin/decorators/${decoratorId}/collect-commission`, { amount, note, bookingId }),
  getBookings:             (params)        => API.get('/api/admin/bookings', { params }),
  resolveDispute:          (id, data)      => API.put(`/api/admin/bookings/${id}/resolve-dispute`, data),
  forceCompleteBooking:    (id, note)      => API.put(`/api/admin/bookings/${id}/force-complete`, { note }),
  addBookingNote:          (id, note)      => API.put(`/api/admin/bookings/${id}/add-note`, { note }),
  getUsers:                (params)        => API.get('/api/admin/users', { params }),
  getUserDetail:           (id)            => API.get(`/api/admin/users/${id}`),
  toggleUser:              (id)            => API.put(`/api/admin/users/${id}/toggle`),
  deleteUser:              (id)            => API.delete(`/api/admin/users/${id}`),
  getReviews:              (params)        => API.get('/api/admin/reviews', { params }),
  hideReview:              (id)            => API.put(`/api/admin/reviews/${id}/hide`),
  showReview:              (id)            => API.put(`/api/admin/reviews/${id}/show`),
  deleteReview:            (id)            => API.delete(`/api/admin/reviews/${id}`),
  broadcast:               (data)          => API.post('/api/admin/broadcast', data),
  getCoupons:              ()              => API.get('/api/admin/coupons'),
  createCoupon:            (data)          => API.post('/api/admin/coupons', data),
  updateCoupon:            (id, data)      => API.put(`/api/admin/coupons/${id}`, data),
  deleteCoupon:            (id)            => API.delete(`/api/admin/coupons/${id}`),
  getAnalyticsOverview:    (period)        => API.get(`/api/admin/analytics/overview?period=${period}`),
  getAnalyticsGrowth:      (period)        => API.get(`/api/admin/analytics/user-growth?period=${period}`),
  getAnalyticsTrend:       (period)        => API.get(`/api/admin/analytics/bookings-trend?period=${period}`),
  getAnalyticsCities:      ()              => API.get('/api/admin/analytics/cities'),
  getAnalyticsTopPerformers: ()            => API.get('/api/admin/analytics/top-performers'),
  getAnalyticsVendorTypes: ()              => API.get('/api/admin/analytics/vendor-types'),
  getAnalyticsHeatmap:     (period)        => API.get(`/api/admin/analytics/heatmap?period=${period}`),
  getAnalyticsActivity:    (limit)         => API.get(`/api/admin/analytics/activity?limit=${limit}`),
  getAnalyticsRetention:   ()              => API.get('/api/admin/analytics/retention'),
};

export default API;